/**
 * useReservationAvailability — TanStack Query hook over
 * `GET /organizations/{slug}/reservations/availability` (SCRUM-350).
 *
 * A thin read over the shipped `ReservationAvailabilityReadPort` — EFFECTIVE,
 * ADR-0026 conflict-group-aware blocked nights for a property + half-open date
 * window (`[from, to)`). The channel-ingest form uses it as a HARD pre-submit
 * overlap block: if any returned blocked night falls in the prospective stay
 * window, submission is blocked (BE `overbooked` quarantine remains the race
 * backstop).
 *
 * The status union MIRRORS `useReservationsList` (loading / ready / forbidden /
 * error) with the same `FORBIDDEN` sentinel idiom and `useCurrentOrg()` slug
 * resolution (RLS-scoped, CLAUDE.md non-negotiable §1). A 403 means the caller
 * lost `booking.reservations.read` since `/auth/me` cached → the form renders a
 * permission-denied affordance rather than a broken block.
 *
 * Deliberate differences from the list hook:
 *   - **Gated on ALL THREE** `propertyId` + `from` + `to` (plus a resolved org
 *     slug). Each one individually absent disables the query — a partial form
 *     window never fires a request.
 *   - **Single-envelope unwrap** (`result.data.data`) → `{ propertyId,
 *     blockedNights }` straight through, NO projection. The endpoint is already
 *     conflict-group-aware server-side; the FE trusts every entry verbatim and
 *     does NO extra same-property filtering.
 *   - **No `keepPreviousData`.** Availability must be FRESH — a stale block (or
 *     stale clear) on a changed window is a correctness bug, not a flicker
 *     annoyance. Each distinct property+window is its own cache entry.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { ReservationAvailabilityDto } from './types.js';
import { bookingQueryKeys } from './queryKeys.js';

/**
 * FE-facing availability-fetch input. All optional so the form can pass a
 * partial window while the user is still filling it in — the query stays
 * disabled until ALL THREE are present.
 */
export interface AvailabilityInput {
  readonly propertyId?: string;
  readonly from?: string;
  readonly to?: string;
}

/**
 * Internal sentinel for the 403 branch — same idiom as
 * `use-reservations-list.ts`. The queryFn returns this symbol on 403 so we can
 * distinguish forbidden from a generic error in the status union.
 */
const FORBIDDEN = Symbol('reservation-availability:forbidden');

export type UseReservationAvailabilityResult =
  | {
      readonly status: 'loading';
    }
  | {
      readonly status: 'ready';
      /** `{ propertyId, blockedNights }` straight from the envelope. */
      readonly data: ReservationAvailabilityDto;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly refetch: () => void;
    };

export function useReservationAvailability(
  input: AvailabilityInput,
): UseReservationAvailabilityResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const { propertyId, from, to } = input;
  // Gated on ALL THREE query args + a resolved org slug.
  const enabled =
    orgSlug !== null &&
    propertyId !== undefined &&
    from !== undefined &&
    to !== undefined;

  const query = useQuery({
    queryKey: [
      ...bookingQueryKeys.availability({ propertyId, from, to }),
      orgSlug,
    ] as const,
    enabled,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET('/organizations/{slug}/reservations/availability', {
        params: {
          path: { slug: orgSlug as string },
          query: {
            propertyId: propertyId as string,
            from: from as string,
            to: to as string,
          },
        },
      });
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/reservations/availability failed with status ${result.response.status}`,
        );
      }
      // Single-envelope unwrap.
      return result.data.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (!enabled || query.isPending) {
    return { status: 'loading' };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }

  return {
    status: 'ready',
    data: query.data,
    refetch,
  };
}
